/**
 * A plain mutable stats object, written every frame from inside the Canvas
 * and read a few times a second by the HUD. Deliberately not React state:
 * per-frame setState would cost more than what it measures.
 */
import { flags } from '../config/flags'

export type Backend = 'pending' | 'WebGPU' | 'WebGL 2 (fallback)' | 'WebGL 2 (forced)'

export const perf = {
  backend: 'pending' as Backend,
  fps: 0,
  frameMs: 0,
  worstMs: 0,
  drawCalls: 0,
  triangles: 0,
  dpr: 1,
  width: 0,
  height: 0,
  gpuLabel: '',
  /** GPU time per frame in ms from timestamp queries; null where the backend can't measure it. */
  gpuMs: null as number | null,
  post: 'pending',
  /** Adaptive quality level and settings (src/quality). */
  quality: 'pending',
  /** Recent quality changes: time, from → to, reason, fps that triggered it. */
  qualityLog: [] as string[],
  errors: [] as string[],
}

// For headless verification scripts (and DevTools): window.__rtkPerf.
;(window as unknown as { __rtkPerf: typeof perf }).__rtkPerf = perf

const MAX_ERRORS = 8

export function pushError(message: string) {
  const line = `${new Date().toISOString().slice(11, 19)} ${message}`
  perf.errors.push(line)
  if (perf.errors.length > MAX_ERRORS) perf.errors.shift()
}

/** Capture uncaught errors and console.error so phone testers can copy them from the HUD. */
export function installErrorCapture() {
  window.addEventListener('error', (e) => pushError(e.message || String(e.error)))
  window.addEventListener('unhandledrejection', (e) => pushError(`unhandled: ${String(e.reason)}`))
  const original = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    pushError(args.map((a) => (a instanceof Error ? a.message : String(a))).join(' '))
    original(...args)
  }
}

/** A plain-text report Teo can paste into the HQ Inbox after a device test. */
export function perfReport(): string {
  return [
    `RTK VOID spike report — ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `UA: ${navigator.userAgent}`,
    `Backend: ${perf.backend}${perf.gpuLabel ? ` (${perf.gpuLabel})` : ''}  tone: ${flags.tone}`,
    `Viewport: ${perf.width}x${perf.height} @ dpr ${perf.dpr.toFixed(2)}`,
    `Post: ${perf.post}`,
    `Quality: ${perf.quality}`,
    `Quality changes: ${perf.qualityLog.length ? '\n  ' + perf.qualityLog.join('\n  ') : 'none'}`,
    `FPS: ${perf.fps.toFixed(0)}  frame: ${perf.frameMs.toFixed(1)} ms  worst (last 1s): ${perf.worstMs.toFixed(1)} ms`,
    `GPU time: ${perf.gpuMs === null ? 'n/a (no timestamp queries on this backend)' : `${perf.gpuMs.toFixed(2)} ms/frame`}`,
    `Draw calls: ${perf.drawCalls}  Triangles: ${perf.triangles}`,
    `Errors: ${perf.errors.length ? '\n  ' + perf.errors.join('\n  ') : 'none'}`,
  ].join('\n')
}
